// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronLeft from "@store/Library/Components/Toolkit/Icons/ChevronLeft";
import ChevronRight from "@store/Library/Components/Toolkit/Icons/ChevronRight";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useEffect, useRef, useState } from "react";
import { css, keyframes } from "styled-components";
import { GreifCarouselStepperProps } from "@store/Library/Components/GreifCarousel/types";

export interface GreifCarouselStepperPresentationProps extends ExpandStyles<GreifCarouselStepperStyles> {}

interface DotsProps extends GreifCarouselStepperPresentationProps {
    slideCount: number;
    selectedIndex: number;
    stepperColor: string;
    onDotClick: (index: number) => void;
}

export interface GreifCarouselStepperStyles {
    stepperDesktop?: InjectableCss;
    stepperMobile?: InjectableCss;
    dotsWrapper?: InjectableCss;
    activeIndicator?: InjectableCss;
    inactiveDot?: InjectableCss;
    inactiveDotShiftForward?: InjectableCss;
    inactiveDotShiftBackward?: InjectableCss;
    inactiveDotShiftAcrossIndicatorForward?: InjectableCss;
    inactiveDotShiftAcrossIndicatorBackward?: InjectableCss;
    arrowsWrapper?: InjectableCss;
    arrowButton?: InjectableCss;
}

const dotSizePx = 6;
const dotGapPx = 8;
const dotSlotShiftDistancePx = dotSizePx + dotGapPx;
const dotSlotShiftDistance = pxToRem(dotSlotShiftDistancePx);
const activeIndicatorWidthPx = 24;
const dotsShiftAnimationDurationMs = 260;
const dotsShiftAnimationDuration = `${dotsShiftAnimationDurationMs}ms`;
const dotAcrossIndicatorShiftDistancePx = dotGapPx + dotSizePx / 2 + activeIndicatorWidthPx / 2;
const dotAcrossIndicatorShiftDistance = pxToRem(dotAcrossIndicatorShiftDistancePx);

const dotsShiftForwardAnimation = keyframes`
    0% {
        transform: translateX(${dotSlotShiftDistance});
    }
    100% {
        transform: translateX(0);
    }
`;

const dotsShiftBackwardAnimation = keyframes`
    0% {
        transform: translateX(calc(-1 * ${dotSlotShiftDistance}));
    }
    100% {
        transform: translateX(0);
    }
`;

const dotsShiftAcrossIndicatorForwardAnimation = keyframes`
    0% {
        transform: translateX(${dotAcrossIndicatorShiftDistance});
    }
    100% {
        transform: translateX(0);
    }
`;

const dotsShiftAcrossIndicatorBackwardAnimation = keyframes`
    0% {
        transform: translateX(calc(-1 * ${dotAcrossIndicatorShiftDistance}));
    }
    100% {
        transform: translateX(0);
    }
`;

export const greifCarouselStepperStyles: GreifCarouselStepperStyles = {
    stepperDesktop: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: ${pxToRem(16)};
            flex-shrink: 0;
            min-width: 0;

            ${breakpoint("sm", "down")} {
                display: none;
            }
        `,
    },
    stepperMobile: {
        css: css`
            display: none;

            ${breakpoint("sm", "down")} {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                gap: ${pxToRem(16)};
                align-self: stretch;
                min-width: 0;
            }
        `,
    },
    dotsWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: ${pxToRem(dotGapPx)};
            min-width: 0;
            overflow: hidden;
        `,
    },
    inactiveDotShiftForward: {
        css: css`
            animation: ${dotsShiftForwardAnimation} ${dotsShiftAnimationDuration} ease;
        `,
    },
    inactiveDotShiftBackward: {
        css: css`
            animation: ${dotsShiftBackwardAnimation} ${dotsShiftAnimationDuration} ease;
        `,
    },
    inactiveDotShiftAcrossIndicatorForward: {
        css: css`
            animation: ${dotsShiftAcrossIndicatorForwardAnimation} ${dotsShiftAnimationDuration} ease;
        `,
    },
    inactiveDotShiftAcrossIndicatorBackward: {
        css: css`
            animation: ${dotsShiftAcrossIndicatorBackwardAnimation} ${dotsShiftAnimationDuration} ease;
        `,
    },
    activeIndicator: {
        css: css`
            display: block;
            width: ${pxToRem(activeIndicatorWidthPx)};
            height: ${pxToRem(6)};
            border-radius: ${pxToRem(100)};
            border: none;
            padding: 0;
            background-color: transparent;
            flex-shrink: 0;
        `,
    },
    inactiveDot: {
        css: css`
            display: block;
            width: ${pxToRem(dotSizePx)};
            height: ${pxToRem(dotSizePx)};
            border-radius: 50%;
            background-color: currentColor;
            opacity: 0.25;
            border: none;
            padding: 0;
            cursor: pointer;
            flex-shrink: 0;

            &:hover {
                opacity: 0.6;
            }

            &:focus-visible {
                opacity: 0.6;
                outline: ${pxToRem(2)} solid currentColor;
                outline-offset: ${pxToRem(2)};
            }
        `,
    },
    arrowsWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: ${pxToRem(8)};
        `,
    },
    arrowButton: {
        css: css`
            box-sizing: border-box;
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            width: ${pxToRem(32)};
            height: ${pxToRem(32)};
            padding: 0;
            background: rgba(255, 255, 255, 0.15);
            border: ${pxToRem(1)} solid currentColor;
            border-radius: 50%;
            flex-shrink: 0;
            cursor: pointer;

            svg {
                width: ${pxToRem(20)};
                height: ${pxToRem(20)};
            }

            &:hover {
                opacity: 0.8;
            }

            &:focus-visible {
                opacity: 0.8;
                outline: ${pxToRem(2)} solid currentColor;
                outline-offset: ${pxToRem(2)};
            }

            &:disabled {
                opacity: 0.35;
                cursor: default;
            }
        `,
    },
};

const maxVisibleDots = 5;
const centeredVisibleSlotIndex = Math.floor(maxVisibleDots / 2);
type StepperShiftDirection = -1 | 0 | 1;

const getVisibleSlideIndexes = (slideCount: number, selectedIndex: number) => {
    if (slideCount <= 0) {
        return [];
    }

    const clampedSelectedIndex = Math.min(Math.max(selectedIndex, 0), slideCount - 1);
    if (slideCount <= maxVisibleDots) {
        return Array.from({ length: slideCount }, (_, index) => index);
    }

    const maxStartIndex = slideCount - maxVisibleDots;
    const startIndex = Math.max(0, Math.min(clampedSelectedIndex - centeredVisibleSlotIndex, maxStartIndex));

    return Array.from({ length: maxVisibleDots }, (_, index) => startIndex + index);
};

const isWrappedSelectionChange = (
    previousSelectedIndex: number | null,
    currentSelectedIndex: number,
    slideCount: number,
) => {
    if (previousSelectedIndex === null || slideCount <= 1) {
        return false;
    }

    return (
        (previousSelectedIndex === slideCount - 1 && currentSelectedIndex === 0) ||
        (previousSelectedIndex === 0 && currentSelectedIndex === slideCount - 1)
    );
};

const getWindowShiftDirection = (
    activeVisibleSlotIndex: number,
    previousWindowStart: number | null,
    currentWindowStart: number,
    wrappedSelectionChange: boolean,
): StepperShiftDirection => {
    if (
        activeVisibleSlotIndex !== centeredVisibleSlotIndex ||
        wrappedSelectionChange ||
        previousWindowStart === null ||
        previousWindowStart === currentWindowStart
    ) {
        return 0;
    }

    return currentWindowStart > previousWindowStart ? 1 : -1;
};

const getInactiveDotAnimationStyle = (
    styles: GreifCarouselStepperStyles,
    windowShiftDirection: StepperShiftDirection,
    slotIndex: number,
    activeVisibleSlotIndex: number,
): InjectableCss | undefined => {
    if (!windowShiftDirection) {
        return undefined;
    }

    const isCrossingIndicatorSlot =
        (windowShiftDirection === 1 && slotIndex === activeVisibleSlotIndex - 1) ||
        (windowShiftDirection === -1 && slotIndex === activeVisibleSlotIndex + 1);

    if (isCrossingIndicatorSlot) {
        return windowShiftDirection === 1
            ? styles.inactiveDotShiftAcrossIndicatorForward
            : styles.inactiveDotShiftAcrossIndicatorBackward;
    }

    return windowShiftDirection === 1 ? styles.inactiveDotShiftForward : styles.inactiveDotShiftBackward;
};

interface DotButtonProps {
    baseStyle?: InjectableCss;
    isSelected: boolean;
    onDotClick: (index: number) => void;
    slideCount: number;
    slideIndex: number;
    stepperColor: string;
}

const DotButton: FC<DotButtonProps> = ({ baseStyle, isSelected, onDotClick, slideCount, slideIndex, stepperColor }) => (
    <StyledWrapper
        {...(baseStyle ?? {})}
        as="button"
        type="button"
        role="tab"
        aria-selected={isSelected}
        aria-current={isSelected ? "true" : undefined}
        aria-label={
            isSelected
                ? translate("Current slide {0}", String(slideIndex + 1))
                : translate("Go to slide {0} of {1}", String(slideIndex + 1), String(slideCount))
        }
        onClick={() => onDotClick(slideIndex)}
        style={isSelected ? { backgroundColor: stepperColor } : { color: stepperColor }}
    />
);

const Dots: FC<DotsProps> = ({
    extendedStyles,
    onDotClick,
    overridedStyles,
    selectedIndex,
    slideCount,
    stepperColor,
}) => {
    const styles = useExpandProps(greifCarouselStepperStyles, extendedStyles, overridedStyles);
    const clampedSelectedIndex = Math.min(Math.max(selectedIndex, 0), Math.max(slideCount - 1, 0));
    const visibleSlideIndexes = getVisibleSlideIndexes(slideCount, clampedSelectedIndex);
    const activeVisibleSlotIndex = visibleSlideIndexes.indexOf(clampedSelectedIndex);
    const [windowShiftDirection, setWindowShiftDirection] = useState<StepperShiftDirection>(0);
    const previousSelectedIndexRef = useRef<number | null>(null);
    const previousWindowStartRef = useRef<number | null>(null);
    const currentWindowStart = visibleSlideIndexes[0] ?? 0;

    useEffect(() => {
        const previousSelectedIndex = previousSelectedIndexRef.current;
        const previousWindowStart = previousWindowStartRef.current;
        previousSelectedIndexRef.current = clampedSelectedIndex;
        previousWindowStartRef.current = currentWindowStart;

        const wrappedSelectionChange = isWrappedSelectionChange(
            previousSelectedIndex,
            clampedSelectedIndex,
            slideCount,
        );
        const nextShiftDirection = getWindowShiftDirection(
            activeVisibleSlotIndex,
            previousWindowStart,
            currentWindowStart,
            wrappedSelectionChange,
        );

        setWindowShiftDirection(nextShiftDirection);
    }, [activeVisibleSlotIndex, clampedSelectedIndex, currentWindowStart, slideCount]);

    useEffect(() => {
        if (!windowShiftDirection) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setWindowShiftDirection(0);
        }, dotsShiftAnimationDurationMs);

        return () => clearTimeout(timeoutId);
    }, [windowShiftDirection]);

    const renderDot = (slideIndex: number, slotIndex: number) => {
        const isSelected = slideIndex === clampedSelectedIndex;
        const dotKey = `dot-${slideIndex}`;
        const dotButton = (
            <DotButton
                baseStyle={isSelected ? styles.activeIndicator : styles.inactiveDot}
                isSelected={isSelected}
                onDotClick={onDotClick}
                slideCount={slideCount}
                slideIndex={slideIndex}
                stepperColor={stepperColor}
            />
        );
        const inactiveDotsAnimationStyle = getInactiveDotAnimationStyle(
            styles,
            windowShiftDirection,
            slotIndex,
            activeVisibleSlotIndex,
        );
        const dotWrapperAnimationStyle = isSelected ? undefined : inactiveDotsAnimationStyle;

        return (
            <StyledWrapper key={dotKey} {...(dotWrapperAnimationStyle ?? {})}>
                {dotButton}
            </StyledWrapper>
        );
    };

    return (
        <StyledWrapper {...styles.dotsWrapper} role="tablist" aria-label={translate("Slides")}>
            {visibleSlideIndexes.map((slideIndex, slotIndex) => renderDot(slideIndex, slotIndex))}
        </StyledWrapper>
    );
};

const GreifCarouselStepper: FC<GreifCarouselStepperProps & GreifCarouselStepperPresentationProps> = ({
    canScrollNext,
    canScrollPrev,
    extendedStyles,
    onDotClick,
    onNext,
    onPrev,
    overridedStyles,
    selectedIndex,
    slideCount,
    stepperColor,
}) => {
    const styles = useExpandProps(greifCarouselStepperStyles, extendedStyles, overridedStyles);
    const dotsProps = {
        slideCount,
        selectedIndex,
        stepperColor,
        onDotClick,
        extendedStyles,
        overridedStyles,
    };

    const renderArrowButton = (direction: "prev" | "next") => {
        const isPrev = direction === "prev";

        return (
            <StyledWrapper
                {...styles.arrowButton}
                as="button"
                type="button"
                onClick={isPrev ? onPrev : onNext}
                disabled={isPrev ? !canScrollPrev : !canScrollNext}
                aria-label={isPrev ? translate("Previous slide") : translate("Next slide")}
                style={{ color: stepperColor }}
            >
                {isPrev ? <ChevronLeft /> : <ChevronRight />}
            </StyledWrapper>
        );
    };

    if (slideCount <= 1) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.stepperDesktop}>
                <Dots {...dotsProps} />
                <StyledWrapper {...styles.arrowsWrapper}>
                    {renderArrowButton("prev")}
                    {renderArrowButton("next")}
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.stepperMobile}>
                {renderArrowButton("prev")}
                <Dots {...dotsProps} />
                {renderArrowButton("next")}
            </StyledWrapper>
        </>
    );
};

export default GreifCarouselStepper;
